export type AppLifecycleEvent = "activate" | "suspend" | "resume" | "resize";

export type LifecyclePort = Readonly<{
  subscribe(listener: (event: AppLifecycleEvent) => void): () => void;
}>;

type LifecycleDocument = Pick<Document, "hidden"> & {
  addEventListener(type: "visibilitychange", listener: () => void): void;
  removeEventListener(type: "visibilitychange", listener: () => void): void;
};

type LifecycleWindow = {
  addEventListener(
    type: "focus" | "pagehide" | "pageshow" | "pointerdown" | "resize",
    listener: () => void,
  ): void;
  removeEventListener(
    type: "focus" | "pagehide" | "pageshow" | "pointerdown" | "resize",
    listener: () => void,
  ): void;
};

export type WebLifecycleEnvironment = Readonly<{
  document: LifecycleDocument;
  window: LifecycleWindow;
}>;

/**
 * Normalizes browser lifecycle signals without deciding what an interruption means.
 * Duplicate browser events are coalesced until the next opposite visibility state.
 */
export function createWebLifecyclePort(
  environment: WebLifecycleEnvironment = { document, window },
): LifecyclePort {
  return Object.freeze({
    subscribe(listener) {
      let suspended = environment.document.hidden;

      const emitSuspend = (): void => {
        if (suspended) return;
        suspended = true;
        listener("suspend");
      };
      const emitResume = (): void => {
        if (!suspended) return;
        suspended = false;
        listener("resume");
      };
      const handleVisibility = (): void => {
        if (environment.document.hidden) emitSuspend();
        else emitResume();
      };
      const handleFocus = (): void => listener("activate");
      const handleResize = (): void => listener("resize");

      environment.document.addEventListener("visibilitychange", handleVisibility);
      environment.window.addEventListener("pagehide", emitSuspend);
      environment.window.addEventListener("pageshow", emitResume);
      environment.window.addEventListener("focus", handleFocus);
      environment.window.addEventListener("pointerdown", handleFocus);
      environment.window.addEventListener("resize", handleResize);

      let subscribed = true;
      return () => {
        if (!subscribed) return;
        subscribed = false;
        environment.document.removeEventListener("visibilitychange", handleVisibility);
        environment.window.removeEventListener("pagehide", emitSuspend);
        environment.window.removeEventListener("pageshow", emitResume);
        environment.window.removeEventListener("focus", handleFocus);
        environment.window.removeEventListener("pointerdown", handleFocus);
        environment.window.removeEventListener("resize", handleResize);
      };
    },
  });
}
